// /* eslint-disable @typescript-eslint/ban-types */
// /* eslint-disable functional/no-return-void */
import { useLazyQuery } from '@apollo/client';
import { Form as FForm, Formik, FormikHelpers, FormikValues } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, Modal, ModalProps, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTimes } from 'react-icons/fa';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { createModal } from 'react-modal-promise';

import { ButtonCancel, ButtonSearch, FieldInput, FormRow, TablePaginatedV8 } from '../index';
import './modal-multi-select.css';

/*

Use As:

Δες Αφης fs-contributor.ts

*/

// -------------------------------------------------------------------------------
// element ModalSelect
// -------------------------------------------------------------------------------

type Props = {
  readonly open: boolean;
  readonly close: (result: any) => void;
  readonly title?: string;
  readonly acceptText?: string;
  readonly denyText?: string;
  readonly type?: string;
  readonly columns: any;
  readonly gqlQuery: any;
  readonly orderBy: string;
  readonly buildWhereFromFilters?: (values: object) => object;
  readonly Filters: (props: FilterProps) => JSX.Element;
  readonly initFilters?: any;
  readonly autoFetchData?: boolean;
  readonly appStore: any;
  /** Προαιρετικός συλλέκτης για έλεγχο επιτρεπόμενων νομών/περιφερειών */
  collector?: any;
} & ModalProps;

const ModalSelect = ({
  open,
  close,
  title = 'Επιλογή',
  acceptText = 'ΟΚ',
  type = 'text',
  columns: initColumns,
  gqlQuery,
  orderBy = 'name',
  buildWhereFromFilters = defaultBuildWhereFromFilters,
  Filters = DefaultFilters,
  initFilters = undefined,
  autoFetchData = false,
  appStore,
  collector,
  getRowId = null,
  idFieldName = 'guid',
  nameFieldName = 'name',
  idValues,
  nameValues,
  size = 'lg',
}: Props) => {
  const { t } = useTranslation();

  // Σταθεροποίηση columns — νέο reference μόνο αν αλλάξει γλώσσα ή columns
  const columns = useMemo(
    () =>
      (initColumns ?? []).map((value) => ({
        ...value,
        header: typeof value.header === 'string' ? t(value.header) : value.header,
        columnName: t(value.columnName),
      })),
    [initColumns, t],
  );

  // Σταθεροποίηση callbacks για να μην προκαλούν re-render στο SelectRecordsForm
  const handleValueSet = useCallback((id) => close(id), [close]);
  const handleClose = useCallback((id) => close(id), [close]);

  if (!initColumns || initColumns.length === 0) {
    return (
      <Modal
        size={size}
        show={open}
        centered
        backdrop="static"
        dialogClassName="modal-multi-select-dialog"
      >
        <Modal.Header style={{ backgroundColor: '#699ac0', color: 'white' }}>
          <Modal.Title>{t(title)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>Δεν υπάρχουν στοιχεία για επιλογή</div>
          <div className="float-end mt-4 pt-3 mb-0">
            <Button
              variant="danger"
              size="lg"
              style={{ paddingTop: '4px', paddingBottom: '4px' }}
              onClick={() => close(false)}
            >
              <FaTimes /> &nbsp; {t('Άκυρο')}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      size={size}
      show={open}
      centered
      backdrop="static"
      dialogClassName="modal-multi-select-dialog"
    >
      <Modal.Header style={{ backgroundColor: '#699ac0', color: 'white' }}>
        <Modal.Title>{t(title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SelectRecordsForm
          onValueSet={handleValueSet}
          onClose={handleClose}
          columns={columns}
          gqlQuery={gqlQuery}
          orderBy={orderBy}
          buildWhereFromFilters={buildWhereFromFilters}
          Filters={Filters}
          initFilters={initFilters}
          autoFetchData={autoFetchData}
          appStore={appStore}
          getRowId={getRowId}
          idFieldName={idFieldName}
          nameFieldName={nameFieldName}
          idValues={idValues}
          nameValues={nameValues}
          collector={collector}
        />
      </Modal.Body>
    </Modal>
  );
};

// --------------------------------------------------------------------------------------------------- //
const SelectRecordsForm = ({
  onValueSet,
  onClose,
  columns,
  gqlQuery,
  orderBy,
  buildWhereFromFilters,
  Filters,
  initFilters,
  autoFetchData = false,
  appStore,
  getRowId,
  idFieldName,
  nameFieldName,
  idValues,
  nameValues,
  collector,
}) => {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<Array<any>>([]);
  const [initPageIndex, setInitPageIndex] = useState(0);
  // Ref για πρόσβαση στο internal table state (setRowSelection μέσω TanStack ref)
  const tableRef = useRef<any>(null);
  const [queryParams, setQueryParams] = useState({
    offset: 10 * initPageIndex,
    limit: 10,
    order: orderBy,
    where: '',
  });

  const [get, { data: queryData, loading }] = useLazyQuery(gqlQuery, {
    fetchPolicy: 'network-only',
  });

  // Με network-only το data μηδενίζεται όσο φορτώνει η επόμενη σελίδα.
  // Κρατάμε το προηγούμενο ώστε ο πίνακας να μην κάνει unmount και να μη χαθεί η επιλογή.
  const lastDataRef = useRef<any>(undefined);
  if (queryData) lastDataRef.current = queryData;
  const data = queryData ?? lastDataRef.current;

  const filtersChanged = async (
    values: FormikValues,
    formikHelpers: FormikHelpers<FormikValues>,
  ) => {
    const where = buildWhereFromFilters(values);
    const newQueryParams = { ...queryParams, offset: 0, where: JSON.stringify(where) };
    setQueryParams(newQueryParams);
    await get({ variables: { ...newQueryParams } });
    setInitPageIndex(0);
  };

  useEffect(() => {
    if (autoFetchData) {
      filtersChanged(initFilters, undefined);
    }
  }, []);

  const onGridPageChange = useCallback(
    (pageIndex, pageSize: number) => {
      setInitPageIndex(pageIndex);
      const newQueryParams = { ...queryParams, offset: pageSize * pageIndex, limit: pageSize };
      setQueryParams(newQueryParams);
      get({ variables: { ...newQueryParams } });
    },
    [get, queryParams],
  );

  /** Σύγκριση id ως string — το GraphQL δίνει number, το initValueId είναι comma-separated string */
  const sameId = (a: unknown, b: unknown) => String(a) === String(b);

  // Το row selection ακολουθεί την τρέχουσα επιλογή, όχι τις αρχικές τιμές,
  // ώστε αν ο πίνακας ξαναδημιουργηθεί να μη γυρίζουν πίσω οι αλλαγές του χρήστη.
  const initRowSelection = useMemo(
    () =>
      selection.reduce(
        (acc, item) => ({ ...acc, [String(item[idFieldName])]: true }),
        {} as Record<string, boolean>,
      ),
    [selection, idFieldName],
  );

  useEffect(() => {
    if (!idValues || !nameValues) return;
    const idArray = String(idValues)
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    // Τα ονόματα ενώνονται με ", " στο FieldMultiMaterialDlg
    const nameArray = String(nameValues)
      .split(',')
      .map((name) => name.trim());
    setSelection(
      idArray.map((id, index) => ({
        [idFieldName]: id,
        [nameFieldName]: nameArray[index] ?? '',
      })),
    );
  }, [idValues, nameValues, idFieldName, nameFieldName]);

  const updateSelected = (rec, isSelected) => {
    const recId = String(rec[idFieldName]);
    if (isSelected) {
      setSelection((prev) => {
        if (prev.some((item) => sameId(item[idFieldName], recId))) return prev;
        return [
          ...prev,
          { [idFieldName]: rec[idFieldName], [nameFieldName]: rec[nameFieldName] },
        ];
      });
    } else {
      setSelection((prev) => prev.filter((item) => !sameId(item[idFieldName], recId)));
      // Αφαιρούμε και από το internal row selection του πίνακα ώστε να μην ξανατροποδοτηθεί
      const table = tableRef.current?.table;
      if (table) {
        table.setRowSelection((prev: Record<string, boolean>) => {
          const next = { ...prev };
          delete next[recId];
          return next;
        });
      }
    }
  };

  const dataRef = useRef(data);
  dataRef.current = data;

  const onRowSelectionChange = useCallback(
    (rowSelection: Record<string, boolean>) => {
      const queryData = dataRef.current;
      const currentData = queryData ? (queryData[Object.keys(queryData)[0]]?.data ?? []) : [];
      // Γραμμές της τρέχουσας σελίδας — οι επιλογές εκτός σελίδας μένουν στο prev
      const currentPageIds = new Set(currentData.map((row) => String(row[idFieldName])));
      const currentPageSelected = currentData
        .filter((row) => rowSelection[String(row[idFieldName])])
        .map((row) => ({
          [idFieldName]: row[idFieldName],
          [nameFieldName]: row[nameFieldName],
        }));

      setSelection((prev) => {
        const kept = prev.filter((item) => !currentPageIds.has(String(item[idFieldName])));
        const merged = [...kept, ...currentPageSelected];
        const seen = new Set<string>();
        return merged.filter((item) => {
          const key = String(item[idFieldName]);
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });
    },
    [idFieldName, nameFieldName],
  );

  const renderSelectedItems = () =>
    selection.map((item) => (
      <Button key={String(item[idFieldName])} variant="warning" className="me-2 mb-2 mt-2">
        {item[nameFieldName]}{' '}
        <FaTimes className="pb-1" onClick={() => updateSelected(item, false)} />
      </Button>
    ));

  // Σταθερό getRowId — αλλάζει μόνο αν αλλάξει το idFieldName
  const getRowIdFn = useCallback((row: any) => row[idFieldName].toString(), [idFieldName]);

  // Σταθερό rowProps — αλλάζει μόνο αν αλλάξει η selection ή ο collector
  const rowPropsFn = useCallback(
    (row: any) => {
      const isSelected = selection.some((item) =>
        sameId(item[idFieldName], row.original[idFieldName]),
      );

      if (!collector) return { className: isSelected ? 'table-warning' : '' };

      const order = row.original;
      let isInvalid = false;
      let reason = '';

      const regionId = order.collectionpoint?.regionId;
      const nomos = order.collectionpoint?.addressNomos;

      // Έλεγχος επιτρεπόμενων νομών για Φωτοκύκλωση
      const allowedNomoi = (collector.collectorsNomoi ?? [])
        .filter((n) => n.isActive)
        .map((n) => n.nomos);
      if (allowedNomoi.length > 0 && nomos && !allowedNomoi.includes(nomos)) {
        isInvalid = true;
        reason = `Ο νομός ${nomos} δεν είναι αποδεκτός για τον συλλέκτη.`;
      }

      // Έλεγχος επιτρεπόμενων περιφερειών (π.χ. Rebattery)
      if (!isInvalid) {
        const allowedRegions = (collector.collectorsRegions ?? [])
          .filter((r) => r.isActive)
          .map((r) => r.regionId);
        if (allowedRegions.length > 0 && regionId && !allowedRegions.includes(regionId)) {
          isInvalid = true;
          reason = `Η περιφέρεια ${regionId} δεν είναι αποδεκτή για τον συλλέκτη.`;
        }
      }

      if (isInvalid) {
        const tooltip = (
          <Tooltip id={`tooltip-${row.id}`}>
            <span>{reason}</span>
          </Tooltip>
        );
        return {
          className: 'table-danger',
          children: (children) => (
            <OverlayTrigger overlay={tooltip} placement="top">
              <tr className="table-danger">{children}</tr>
            </OverlayTrigger>
          ),
        };
      }

      return { className: isSelected ? 'table-warning' : '' };
    },
    [selection, collector, idFieldName],
  );

  return (
    <div className="modal-multi-select-form">
      <div className="modal-multi-select-filters">
        <Filters onChange={filtersChanged} initialValues={initFilters} />
      </div>
      {selection?.length > 0 && (
        <div className="modal-multi-select-selection">{renderSelectedItems()}</div>
      )}
      <div className="modal-multi-select-grid">
        <LoadingOverlay active={loading} spinner text="Παρακαλώ περιμένετε...">
          {data ? (
            <TablePaginatedV8
              ref={tableRef}
              id="modal-select"
              appStore={appStore}
              columns={columns}
              data={data[Object.keys(data)[0]]?.data}
              dataCount={data[Object.keys(data)[0]]?.recordCount}
              onPageChange={onGridPageChange}
              initPageIndex={initPageIndex}
              rowProps={rowPropsFn}
              style={{
                minWidth: 'inherit !important',
                overflowY: 'auto',
              }}
              onRowSelectionChange={onRowSelectionChange}
              initSorting={[{ desc: true, id: 'name' }]}
              fixedPageSize={10}
              getRowId={getRowIdFn}
              initRowSelection={initRowSelection}
            />
          ) : (
            <div
              style={{
                minHeight: '40vh',
                background: 'rgba(0, 0, 0, 0.03)',
                borderRadius: '0.3rem',
              }}
            />
          )}
        </LoadingOverlay>
      </div>
      <div className="modal-multi-select-actions d-flex justify-content-end mt-3 mb-0">
        <Button
          className="me-3"
          variant="primary"
          size="lg"
          style={{ paddingTop: '4px', paddingBottom: '4px', minWidth: '7rem' }}
          onClick={() => onValueSet(selection)}
          disabled={selection.length === 0}
        >
          <FaCheck /> &nbsp; {t('OK')}
        </Button>
        <ButtonCancel onClick={() => onClose(false)} />
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------------------------------- //
// Filters
//
const defaultBuildWhereFromFilters = (values) => {
  if (!values.searchText) return null;
  const where = { name: { $like: '%' + values.searchText + '%' } };
  return where;
};

// ----------- Filters ------------------ //
type OnChangeFunc = (values: FormikValues, formikHelpers: FormikHelpers<FormikValues>) => void;
type FilterProps = {
  readonly onChange: OnChangeFunc;
  readonly initialValues?: any;
};
const DefaultFilters: (props: FilterProps) => JSX.Element = ({ onChange, initialValues }) => {
  return (
    <Formik initialValues={initialValues ? initialValues : { searchText: '' }} onSubmit={onChange}>
      <Form as={FForm}>
        <FormRow className="mb-3 align-items-end">
          <Col sm={6} lg={8} xl={8} className="ps-0 pe-0">
            <FieldInput as={Col} name="searchText" id="searchText" label="Επωνυμία" type="search" />
          </Col>
          <Col className="ps-0 pe-0 me-3 ">
            <ButtonSearch size="lg" type="submit" className="float-end mb-2" />
          </Col>
        </FormRow>
      </Form>
    </Formik>
  );
};

// --------------------------------------------------------------------------------------------------- //
// exports
// --------------------------------------------------------------------------------------------------- //

const promiseModalMultiSelect = createModal(ModalSelect);

export { promiseModalMultiSelect };
export type { FilterProps, OnChangeFunc };
